import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';
import { scheduleService } from '../services/scheduleService';
import { useNotificationStore } from '../store/notificationStore';
import { ScheduleExecutionHistory } from '../components/schedules/ScheduleExecutionHistory';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';
import type { ScheduleDto, ScheduleType } from '../types/schedule';
import { ScheduleTypes } from '../types/schedule';

/**
 * Schedule detail page component.
 */
export const ScheduleDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  const [schedule, setSchedule] = useState<ScheduleDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSchedule = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await scheduleService.getSchedule(id);
      setSchedule(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load schedule');
      showNotification(err.message || 'Failed to load schedule', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      await scheduleService.deleteSchedule(id);
      showNotification('Schedule deleted successfully', 'success');
      navigate('/schedules');
    } catch (err: any) {
      showNotification(err.message || 'Failed to delete schedule', 'error');
    }
  };

  const handleToggleActive = async () => {
    if (!id || !schedule) return;

    try {
      if (schedule.isActive) {
        await scheduleService.disableSchedule(id);
        showNotification('Schedule disabled successfully', 'success');
      } else {
        await scheduleService.enableSchedule(id);
        showNotification('Schedule enabled successfully', 'success');
      }
      loadSchedule();
    } catch (err: any) {
      showNotification(err.message || 'Failed to update schedule', 'error');
    }
  };

  const formatScheduleType = (type: ScheduleType): string => {
    switch (type) {
      case ScheduleTypes.Cron:
        return 'Cron';
      case ScheduleTypes.Interval:
        return 'Interval';
      case ScheduleTypes.OneTime:
        return 'One-Time';
      default:
        return type;
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <PageDetailLayout
      title={schedule?.name || 'Schedule'}
      breadcrumbs={[{ label: 'Schedules', href: '/schedules' }, { label: schedule?.name || 'Schedule' }]}
      backTo={{ label: 'Schedules', href: '/schedules' }}
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      status={schedule ? { label: schedule.isActive ? 'Active' : 'Inactive', color: schedule.isActive ? 'success' : 'default' } : undefined}
      chips={schedule ? [
        { label: formatScheduleType(schedule.scheduleType) },
      ] : []}
      actions={schedule ? [
        {
          icon: schedule.isActive ? <PauseIcon /> : <PlayArrowIcon />,
          label: schedule.isActive ? 'Disable' : 'Enable',
          onClick: handleToggleActive,
        },
        { icon: <EditIcon />, label: 'Edit', onClick: () => navigate(`/schedules/${schedule.id}/edit`) },
        { icon: <DeleteIcon />, label: 'Delete', onClick: handleDelete, color: 'error' as const },
      ] : []}
    >
      {schedule && (
        <>
          {/* Schedule Information Card */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Description
                  </Typography>
                  <Typography variant="body1">{schedule.description || 'No description'}</Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Agent
                  </Typography>
                  <Typography variant="body1">{schedule.agentName}</Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Schedule Type
                  </Typography>
                  <Chip label={formatScheduleType(schedule.scheduleType)} size="small" />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Status
                  </Typography>
                  <Chip
                    label={schedule.isActive ? 'Active' : 'Inactive'}
                    color={schedule.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Grid>

                {schedule.scheduleType === ScheduleTypes.Cron && schedule.cronExpression && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                      Cron Expression
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace">
                      {schedule.cronExpression}
                    </Typography>
                  </Grid>
                )}

                {schedule.scheduleType === ScheduleTypes.OneTime && schedule.scheduledDateTime && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                      Scheduled Date/Time
                    </Typography>
                    <Typography variant="body1">{formatDate(schedule.scheduledDateTime)}</Typography>
                  </Grid>
                )}

                {schedule.scheduleType === ScheduleTypes.Interval && schedule.interval && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                      Interval
                    </Typography>
                    <Typography variant="body1">{schedule.interval}</Typography>
                  </Grid>
                )}

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Time Zone
                  </Typography>
                  <Typography variant="body1">{schedule.timeZone}</Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Next Execution
                  </Typography>
                  <Typography variant="body1">{formatDate(schedule.nextExecutionAt)}</Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Last Execution
                  </Typography>
                  <Typography variant="body1">{formatDate(schedule.lastExecutionAt)}</Typography>
                </Grid>

                {schedule.maxRetries && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                      Max Retries
                    </Typography>
                    <Typography variant="body1">{schedule.maxRetries}</Typography>
                  </Grid>
                )}

                {schedule.retryDelay && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                      Retry Delay
                    </Typography>
                    <Typography variant="body1">{schedule.retryDelay}</Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Execution History Card */}
          <Card>
            <CardContent>
              <ScheduleExecutionHistory scheduleId={schedule.id} />
            </CardContent>
          </Card>
        </>
      )}
    </PageDetailLayout>
  );
};
